import newRelicAgent from '../../../newRelic';
import { Logger, nullLogger } from './Logger';

export interface StorageEstimate {
	usage: number;
	quota: number;
	usagePercentage: number;
}

export enum StorageQuotaLevel {
	NORMAL = 'normal',
	INFO = 'info',
	WARNING = 'warning',
	CRITICAL = 'critical'
}

export interface StorageQuotaStatus {
	estimate: StorageEstimate;
	level: StorageQuotaLevel;
	timestamp: number;
}

type StorageQuotaListener = ( status: StorageQuotaStatus ) => void;

interface StorageBreakdown {
	indexedDB?: number;
	caches?: number;
	total: number;
}

const DEFAULT_MONITORING_INTERVAL = 60000; // 60 seconds

class StorageMonitoringService {
	private listeners: Set<StorageQuotaListener> = new Set();
	private currentStatus: StorageQuotaStatus | null = null;
	private monitoringInterval: NodeJS.Timeout | null = null;
	private isMonitoring = false;
	private logger: Logger = process.env.NODE_ENV === 'production' ? nullLogger : console;

	private readonly THRESHOLDS = {
		INFO: 50,
		WARNING: 75,
		CRITICAL: 90
	};

	private isStorageAPISupported(): boolean {
		return 'storage' in navigator && 'estimate' in navigator.storage;
	}

	async getStorageEstimate(): Promise<StorageEstimate | null> {
		if ( !this.isStorageAPISupported() ) {
			this.logger.warn( 'Storage API not supported in this browser' );
			return null;
		}

		try {
			const estimate = await navigator.storage.estimate();
			const usage = estimate.usage || 0;
			const quota = estimate.quota || 0;
			const usagePercentage = quota > 0 ? ( usage / quota ) * 100 : 0;

			return {
				usage,
				quota,
				usagePercentage
			};
		} catch ( error ) {
			this.logger.error( 'Failed to get storage estimate:', error );
			this.reportErrorToNewRelic( error as Error, 'getStorageEstimate' );
			return null;
		}
	}

	async getStorageBreakdown(): Promise<StorageBreakdown | null> {
		if ( !this.isStorageAPISupported() ) {
			return null;
		}

		try {
			const estimate = await navigator.storage.estimate();
			const usage = estimate.usage || 0;

			// Get usage details if available (Chrome/Edge support this)
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const { usageDetails } = estimate as any;

			if ( usageDetails ) {
				return {
					indexedDB: usageDetails.indexedDB || 0,
					caches: usageDetails.caches || 0,
					total: usage
				};
			}

			return {
				total: usage
			};
		} catch ( error ) {
			this.logger.error( 'Failed to get storage breakdown:', error );
			return null;
		}
	}

	private getQuotaLevel( usagePercentage: number ): StorageQuotaLevel {
		if ( usagePercentage >= this.THRESHOLDS.CRITICAL ) {
			return StorageQuotaLevel.CRITICAL;
		}
		if ( usagePercentage >= this.THRESHOLDS.WARNING ) {
			return StorageQuotaLevel.WARNING;
		}
		if ( usagePercentage >= this.THRESHOLDS.INFO ) {
			return StorageQuotaLevel.INFO;
		}
		return StorageQuotaLevel.NORMAL;
	}

	async checkQuota(): Promise<StorageQuotaStatus | null> {
		this.logger.log( 'Checking quota' );

		const estimate = await this.getStorageEstimate();

		if ( !estimate ) {
			this.logger.warn( 'No estimate found' );
			return null;
		}

		const level = this.getQuotaLevel( estimate.usagePercentage );
		const status: StorageQuotaStatus = {
			estimate,
			level,
			timestamp: Date.now()
		};

		this.logger.log( 'Quota status:', status );

		// Report to New Relic
		this.reportQuotaMetricsToNewRelic( status );

		// Check if level changed
		const levelChanged = this.currentStatus?.level !== level;
		this.currentStatus = status;

		// Notify listeners if level changed
		if ( levelChanged ) {
			this.notifyListeners( status );
		}

		return status;
	}

	startMonitoring( intervalMs: number = DEFAULT_MONITORING_INTERVAL ): void {
		if ( this.isMonitoring ) {
			this.logger.warn( 'Storage monitoring already started' );
			return;
		}

		if ( !this.isStorageAPISupported() ) {
			this.logger.warn( 'Cannot start monitoring: Storage API not supported' );
			return;
		}

		this.isMonitoring = true;

		this.checkQuota();

		this.monitoringInterval = setInterval( () => {
			this.checkQuota();
		}, intervalMs );

		this.logger.log( `Storage monitoring started (interval: ${intervalMs}ms)` );
	}

	/**
	 * Stop monitoring storage quota
	 */
	stopMonitoring(): void {
		if ( this.monitoringInterval ) {
			clearInterval( this.monitoringInterval );
			this.monitoringInterval = null;
		}
		this.isMonitoring = false;
		this.logger.log( 'Storage monitoring stopped' );
	}

	subscribe( listener: StorageQuotaListener ): () => void {
		this.listeners.add( listener );

		// Send current status immediately if available
		if ( this.currentStatus ) {
			listener( this.currentStatus );
		}

		// Return unsubscribe function
		return () => {
			this.listeners.delete( listener );
		};
	}

	private notifyListeners( status: StorageQuotaStatus ): void {
		this.listeners.forEach( ( listener ) => {
			try {
				listener( status );
			} catch ( error ) {
				this.logger.error( 'Error in storage quota listener:', error );
			}
		} );
	}

	private reportQuotaMetricsToNewRelic( status: StorageQuotaStatus ): void {
		if ( !newRelicAgent ) { return; }

		this.logger.log( 'Reporting quota metrics to New Relic', status );

		try {
			newRelicAgent.addPageAction( 'StorageQuotaCheck', {
				usage: status.estimate.usage,
				quota: status.estimate.quota,
				usagePercentage: status.estimate.usagePercentage,
				level: status.level,
				usageMB: ( status.estimate.usage / ( 1024 * 1024 ) ).toFixed( 2 ),
				quotaMB: ( status.estimate.quota / ( 1024 * 1024 ) ).toFixed( 2 )
			} );

			// Report as custom metric for dashboards
			const agent = newRelicAgent as unknown as { setCustomAttribute?: ( name: string, value: unknown ) => void };
			if ( agent.setCustomAttribute ) {
				agent.setCustomAttribute( 'storage.usage', status.estimate.usage );
				agent.setCustomAttribute( 'storage.quota', status.estimate.quota );
				agent.setCustomAttribute( 'storage.usagePercentage', status.estimate.usagePercentage );
				agent.setCustomAttribute( 'storage.level', status.level );
			}

			if ( status.level === StorageQuotaLevel.WARNING || status.level === StorageQuotaLevel.CRITICAL ) {
				this.logger.warn(
					`[Storage Quota ${status.level.toUpperCase()}] Usage: ${status.estimate.usagePercentage.toFixed( 2 )}%`
				);

				newRelicAgent.addPageAction( 'StorageQuotaThresholdExceeded', {
					level: status.level,
					usagePercentage: status.estimate.usagePercentage,
					usage: status.estimate.usage,
					quota: status.estimate.quota
				} );
			}
		} catch ( error ) {
			this.logger.error( 'Failed to report to New Relic', error );
		}
	}

	reportErrorToNewRelic( error: Error, context: string ): void {
		if ( !newRelicAgent ) { return; }

		try {
			newRelicAgent.noticeError( error, {
				context,
				storageRelated: true,
				currentUsagePercentage: this.currentStatus?.estimate.usagePercentage
			} );
		} catch ( err ) {
			this.logger.error( 'Failed to report error to New Relic', err );
		}
	}

	getCurrentStatus(): StorageQuotaStatus | null {
		return this.currentStatus;
	}

	async requestPersistentStorage(): Promise<boolean> {
		if ( !( 'storage' in navigator && 'persist' in navigator.storage ) ) {
			this.logger.warn( 'Persistent storage not supported' );
			return false;
		}

		try {
			const isPersisted = await navigator.storage.persist();

			if ( newRelicAgent ) {
				newRelicAgent.addPageAction( 'PersistentStorageRequest', {
					granted: isPersisted
				} );
			}

			return isPersisted;
		} catch ( error ) {
			this.logger.error( 'Failed to request persistent storage', error );
			this.reportErrorToNewRelic( error as Error, 'requestPersistentStorage' );
			return false;
		}
	}

	async isPersisted(): Promise<boolean> {
		if ( !( 'storage' in navigator && 'persisted' in navigator.storage ) ) {
			return false;
		}

		try {
			return await navigator.storage.persisted();
		} catch ( error ) {
			this.logger.error( 'Failed to check if persisted', error );
			return false;
		}
	}
}

export const storageMonitoringService = new StorageMonitoringService();
export default storageMonitoringService;
